import React, { useEffect, useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import "react-datepicker/dist/react-datepicker.css";
import { uk } from "date-fns/locale";
import * as bootstrap from "bootstrap";
import ProductAutocomplete from "../components/ProductAutocomplete";
import CatalogTable from "../components/CatalogTable";
import { AppCheckbox, CheckboxHolder } from "../components/form";

const ACCOUNTS = [
    { key: "lava1", label: "Lava Studio #1" },
    { key: "lava2", label: "Lava Studio #2" },
    { key: "lava3", label: "Lava Studio #3" },
    { key: "lava4", label: "Lava Studio #4" },
];

function groupAds(ads) {
    const map = {};

    ads.forEach(ad => {
        const key = `${ad.campaign_id}_${ad.adset_id}_${ad.ad_id}`;

        if (!map[key]) {
            map[key] = {
                ...ad,
                spend: 0,
                clicks: 0,
                impressions: 0
            };
        }

        map[key].spend += Number(ad.spend || 0);
        map[key].clicks += Number(ad.clicks || 0);
        map[key].impressions += Number(ad.impressions || 0);
    });

    return Object.values(map);
}


const FacebookPage = ({ user }) => {

    const role = user?.role;
    const hasAnyRole = (...roles) => roles.includes(role);

    const [modalContent, setModalContent] = useState(null);
    const [products, setProducts] = useState([]);

    const today = dayjs();
    const defaultTo = today.subtract(1, "day").toDate();
    const defaultFrom = today.subtract(1, "day").toDate();

    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(defaultTo);
    const [source, setSource] = useState("lava2");

    const [loading, setLoading] = useState(false);

    const [catalogStats, setCatalogStats] = useState([]);
    const [openedCatalogAdId, setOpenedCatalogAdId] = useState(null);

    const [dailyAds, setDailyAds] = useState([]);

    const [showCatalogs, setShowCatalogs] = useState(false);
    const [showTagged, setShowTagged] = useState(true);

    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState("asc"); // asc | desc

    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 991px)");

        const handleChange = (e) => {
            setIsMobile(e.matches);
        };

        setIsMobile(mediaQuery.matches);

        mediaQuery.addEventListener("change", handleChange);

        return () => {
            mediaQuery.removeEventListener("change", handleChange);
        };
    }, []);




    // ------------------- PRODUCT TAG PARSER -------------------
    function extractProductIds(name = "") {
        const match = name.match(/\{([^}]+)\}/);
        if (!match) return [];
        return match[1]
            .split(",")
            .map((x) => parseInt(x.trim(), 10))
            .filter(Boolean);
    }

    function buildName(base, ids) {
        if (ids.length === 0) return base.trim();
        return `${base.trim()} {${ids.join(",")}}`;
    }

    function parseAdName(name = "") {
        const match = name.match(/\{(.+?)}/);
        if (!match) return { base: name.trim(), ids: [] };

        const ids = match[1]
            .split(",")
            .map((x) => Number(x.trim()))
            .filter(Boolean);
        const base = name.split("{")[0].trim();

        return { base, ids };
    }

    function enrichWithProducts(ads, productsList) {
        return ads.map((ad) => {
            const ids = extractProductIds(ad.ad_name || "");
            const selectedProducts = ids
                .map((id) => {
                    const p = productsList.find((pp) => pp.product_id === id);
                    if (!p) return null;
                    return { id: p.product_id, name: p.name };
                })
                .filter(Boolean);

            return {
                ...ad,
                selectedProducts,
            };
        });
    }

    // ----------------------- Дані з БД ----------------------
    const loadRangeFromDB = async () => {
        try {
            setLoading(true);

            const res = await axios.get("/api/fb/daily-range", {
                params: {
                    from: dayjs(from).format("YYYY-MM-DD"),
                    to: dayjs(to).format("YYYY-MM-DD"),
                    source
                }
            });

            const rawAds = res.data;

            // агрегируем уже на клиенте:
            const grouped = groupAds(rawAds);

            // добавляем инфу о товарах:
            const enriched = enrichWithProducts(grouped, products);

            setDailyAds(enriched);

        } catch (e) {
            console.error("LOAD RANGE ERROR:", e);
            alert("Помилка завантаження діапазону з БД");
        } finally {
            setLoading(false);
        }
    };

    // Если товары подгрузились позже — дотягиваем selectedProducts
    useEffect(() => {
        if (!products.length || !dailyAds.length) return;

        setDailyAds((prev) => enrichWithProducts(prev, products));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    const deleteDaily = async () => {
        try {
            await axios.post("/api/fb/delete-daily", {
                from: dayjs(from).format("YYYY-MM-DD"),
                to: dayjs(to).format("YYYY-MM-DD"),
                source,
            });

            setDailyAds([]);
            alert("Очищено ✔️");
        } catch (e) {
            console.error("DELETE DAILY ERROR:", e);
            alert("Помилка очищення");
        }
    };

    const loadCatalogStats = async (ad) => {
        if (openedCatalogAdId === ad.ad_id) {
            setOpenedCatalogAdId(null);
            setCatalogStats([]);
            return;
        }

        try {
            const res = await axios.get("/api/fb/catalog-stats", {
                params: {
                    catalog_id: ad.ad_id,
                    from: dayjs(from).format("YYYY-MM-DD"),
                    to: dayjs(to).format("YYYY-MM-DD"),
                    source,
                },
            });

            setCatalogStats(res.data);
            setOpenedCatalogAdId(ad.ad_id);
        } catch (e) {
            console.error("Помилка catalog-stats", e);
            alert("Помилка отримання статистики каталогу");
        }
    };

    // ----------------------- PRODUCTS ----------------------------
    async function fetchProducts() {
        try {
            const res = await axios.get("/api/products/unique");
            setProducts(res.data);
        } catch (e) {
            console.error("Помилка завантаження товарів", e);
        }
    }

    useEffect(() => {
        fetchProducts();
    }, []);

    // Поддержка bootstrap tooltip (если нужно)
    useEffect(() => {
        const tooltipTriggerList = document.querySelectorAll(
            "[data-bs-toggle=\"tooltip\"]",
        );
        tooltipTriggerList.forEach((el) => new bootstrap.Tooltip(el));
    }, [dailyAds]);

    // Сброс видео при закрытии модалки
    useEffect(() => {
        const modalEl = document.getElementById("creativeModal");

        if (!modalEl) return;

        const handler = () => {
            const videos = modalEl.querySelectorAll("video");
            videos.forEach((v) => {
                v.pause();
                v.currentTime = 0;
            });
        };

        modalEl.addEventListener("hidden.bs.modal", handler);

        return () => modalEl.removeEventListener("hidden.bs.modal", handler);
    }, []);

    // ----------------------- Обновление имени + тегов ---------------------

    async function updateAdEverywhere(ad, newIds) {
        const { base } = parseAdName(ad.ad_name);
        const newName = buildName(base, newIds);

        // 1 — обновляем в Facebook
        await axios.post("/api/fb/rename-ad", {
            ad_id: ad.ad_id,
            new_name: newName,
        });

        // 2 — обновляем в БД
        await axios.post("/api/fb/update-db-ad", {
            ad_id: ad.ad_id,
            ad_name: newName,
            selected_products: newIds,
        });

        // 3 — обновляем состояние React
        setDailyAds((prev) =>
            prev.map((x) =>
                x.ad_id === ad.ad_id
                    ? {
                        ...x,
                        ad_name: newName,
                        selectedProducts: newIds.map((id) => ({
                            id,
                            name:
                                products.find((p) => p.product_id === id)?.name || "???",
                        })),
                    }
                    : x,
            ),
        );
    }

    // ----------------------- Thumbnail Click ---------------------
    const handleThumbnailClick = async (ad) => {
        console.log("handleThumbnailClick ad", ad);

        // FB VIDEO → открыть facebook
        if (ad.media_type === "fb_video") {
            window.open(ad.facebook_video_url, "_blank");
            return;
        }

        // IG CAROUSEL → модалка со слайдером
        if (ad.media_type === "carousel") {
            setModalContent({ ...ad, carousel: ad.carousel || [], slide: 0 });
            new bootstrap.Modal(document.getElementById("creativeModal")).show();
            return;
        }

        // Каталог → показываем таблицу каталога
        if (ad.media_type === "catalog") {
            await loadCatalogStats(ad);
            return;
        }

        // IG VIDEO / IMAGE → открываем модалку
        setModalContent({ ...ad, bigImage: null });
        const modal = new bootstrap.Modal(document.getElementById("creativeModal"));
        modal.show();

        setTimeout(async () => {
            try {
                if (ad.creative_id) {
                    const res = await axios.get(`/api/fb/creative/${ad.creative_id}`);
                    if (res.data.bigImage) {
                        setModalContent((prev) => ({
                            ...prev,
                            bigImage: res.data.bigImage,
                        }));
                    }
                }
            } catch (e) {
                console.error("BIG IMAGE LOAD ERROR", e);
            }
        }, 50);
    };

    // === TOTALS ===
    const totals = dailyAds.reduce(
        (acc, ad) => {
            acc.spend += Number(ad.spend || 0);
            acc.clicks += Number(ad.clicks || 0);
            acc.impressions += Number(ad.impressions || 0);
            return acc;
        },
        { spend: 0, clicks: 0, impressions: 0 },
    );

    const saveDailyToDB = async () => {
        try {
            setLoading(true);

            const res = await axios.post("/api/fb/save-daily", {
                source,
                from: dayjs(from).format("YYYY-MM-DD"),
                to: dayjs(to).format("YYYY-MM-DD"),
            });

            console.log("SAVE DAILY RESULT:", res.data);
            alert("Збережено!");
            await loadRangeFromDB();
        } catch (e) {
            console.error("SAVE DAILY ERROR:", e);
            alert("Помилка збереження");
        } finally {
            setLoading(false);
        }
    };

    function sortAds(list) {
        if (!sortField) return list;

        return [...list].sort((a, b) => {
            let v1 = a[sortField];
            let v2 = b[sortField];

            // строковые поля → строковая сортировка
            if (typeof v1 === "string") v1 = v1.toLowerCase();
            if (typeof v2 === "string") v2 = v2.toLowerCase();

            // сравнение
            if (v1 < v2) return sortDir === "asc" ? -1 : 1;
            if (v1 > v2) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }

    const filteredAds = sortAds(
        dailyAds.filter(ad => {
            if (!showCatalogs && ad.media_type === "catalog") return false;

            const hasTags = (ad.selectedProducts?.length ?? 0) > 0;
            if (!showTagged && hasTags) return false;

            return true;
        })
    );


    function handleSort(field) {
        if (sortField === field) {
            // повторный клик → сменить направление
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            // новая колонка
            setSortField(field);
            setSortDir("asc");
        }
    }

    const MobileAdsList = ({
                               ads,
                               handleThumbnailClick,
                               updateAdEverywhere,
                           }) => {
        return (
            <div className="d-flex flex-column gap-3">
                {ads.map((ad) => (
                    <div key={ad.ad_id} className="card shadow-sm p-3">

                        <div className="d-flex gap-3">

                            <div
                                style={{ width: 80, height: 80 }}
                                className="rounded overflow-hidden position-relative"
                                onClick={() => handleThumbnailClick(ad)}
                            >
                                <img
                                    src={ad.thumbnail_url}
                                    className="w-100 h-100"
                                    style={{ objectFit: "cover" }}
                                    alt=""
                                />

                                {(ad.media_type === "video" ||
                                    ad.media_type === "fb_video") && (
                                    <div className="position-absolute top-50 start-50 translate-middle">
                                        ▶
                                    </div>
                                )}
                            </div>

                            <div className="flex-grow-1">

                                <div className="fw-semibold small mb-2">
                                    {ad.ad_name}
                                </div>

                                <div className="d-flex mb-3 small text-muted">
                                    <span className="px-2 ps-0">👁 {ad.impressions}</span>
                                    <span className="px-2">👆 {ad.clicks}</span>
                                    <span className="px-2">
                                      ${Number(ad.spend || 0).toFixed(2)}
                                    </span>
                                </div>
                                <div>
                                    <ProductAutocomplete
                                        products={products}
                                        onSelect={(product) => {
                                            const { ids } = parseAdName(ad.ad_name);
                                            if (!ids.includes(product.product_id)) {
                                                const newIds = [...ids, product.product_id];
                                                updateAdEverywhere(ad, newIds);
                                            }
                                        }}
                                    />
                                </div>

                            </div>
                        </div>

                        {/* Теги */}
                        {ad.selectedProducts?.length > 0 && (
                            <div className="mt-2 d-flex flex-wrap gap-1">
                                {ad.selectedProducts.map((p) => (
                                    <span key={p.id} className="badge bg-primary">
                  {p.name}
                                        <span
                                            className="ms-1"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                const { ids } = parseAdName(ad.ad_name);
                                                const newIds = ids.filter((id) => id !== p.id);
                                                updateAdEverywhere(ad, newIds);
                                            }}
                                        >
                    ×
                  </span>
                </span>
                                ))}
                            </div>
                        )}

                    </div>
                ))}
            </div>
        );
    };




    return (
        <div className="fb-ads container mt-4">
            <h1 className="mb-4">Facebook ads</h1>

            {/* Фильтры */}
            <div className="d-lg-flex mb-3  mb-3 align-items-center gap-3">
                <DatePicker
                    selected={from}
                    onChange={setFrom}
                    locale={uk}
                    dateFormat="yyyy-MM-dd"
                    className="form-control w-auto mb-3 m-lg-0"
                />
                <DatePicker
                    selected={to}
                    onChange={setTo}
                    locale={uk}
                    dateFormat="yyyy-MM-dd"
                    minDate={from}
                    className="form-control w-auto ms-2 mb-3 m-lg-0"
                />
                <select
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    className="form-select w-auto border-1 border-dark border-radius-sm order-lg-first"
                >
                    {ACCOUNTS.map((acc) => (
                        <option key={acc.key} value={acc.key}>
                            {acc.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="d-flex mb-3 gap-2">
                <button onClick={loadRangeFromDB} className="btn btn-primary ms-0">
                    Завантажити з БД
                </button>
                {hasAnyRole("admin") && !isMobile && (
                <><button onClick={saveDailyToDB} className="btn btn-success">
                    Зберегти в БД
                </button>
                <button onClick={deleteDaily} className="btn btn-danger">
                    Очистити БД
                </button>
                </>
            )}
            </div>
            <CheckboxHolder className="mb-3" formClassName="d-flex flex-wrap gap-4 align-items-center mb-0">
                <AppCheckbox
                    id="fb-show-catalogs"
                    label="Показувати каталоги"
                    checked={showCatalogs}
                    onChange={() => setShowCatalogs((prev) => !prev)}
                />
                <AppCheckbox
                    id="fb-show-tagged"
                    label="Показувати з тегами"
                    checked={showTagged}
                    onChange={() => setShowTagged((prev) => !prev)}
                />
            </CheckboxHolder>

            {loading && <div className="mb-3">Завантаження...</div>}

            {dailyAds.length > 0 && (
                <>
                    {/* Тотал сверху */}
                    <div className="mb-2 fw-bold text-end">
                        Усього: ${totals.spend.toFixed(2)} · 👆 {totals.clicks} · 👁{" "}
                        {totals.impressions}
                    </div>
                    {isMobile ? (
                        <MobileAdsList
                            ads={filteredAds}
                            handleThumbnailClick={handleThumbnailClick}
                            updateAdEverywhere={updateAdEverywhere}
                        />
                        ) : (
                        <table className="table table-light table-hover align-middle mt-2">
                        <thead className="bg-light">
                        <tr className="text-center">
                        <th style={{ width: "80px" }}>Креатив</th>
                <th onClick={() => handleSort("ad_name")} className="text-start" style={{cursor: "pointer"}}>
            Назва {sortField === "ad_name" && (sortDir === "asc" ? "↑" : "↓")}
        </th>
    <th onClick={() => handleSort("spend")} className="text-end" style={{cursor: "pointer", whiteSpace: "nowrap"}}>
        Витрачено {sortField === "spend" && (sortDir === "asc" ? "↑" : "↓")}
    </th>
    <th onClick={() => handleSort("clicks")} className="text-end" style={{cursor: "pointer", whiteSpace: "nowrap"}}>
        Кліки {sortField === "clicks" && (sortDir === "asc" ? "↑" : "↓")}
    </th>
    <th onClick={() => handleSort("impressions")} className="text-end" style={{cursor: "pointer", whiteSpace: "nowrap"}}>
        Покази {sortField === "impressions" && (sortDir === "asc" ? "↑" : "↓")}
    </th>
</tr>
</thead>

    <tbody>
    {sortAds(
        dailyAds.filter(ad => {
            if (!showCatalogs && ad.media_type === "catalog") return false;
            const hasTags = (ad.selectedProducts?.length ?? 0) > 0;
            if (!showTagged && hasTags) return false;
            return true;
        })
    ).map((ad) => (
        <React.Fragment key={ad.ad_id}>
            <tr>
                {/* thumbnail */}
                <td className="td-thumbnail">
                    <div
                        style={{ width: "70px", height: "70px" }}
                        className="position-relative rounded overflow-hidden cursor-pointer"
                        onClick={() => handleThumbnailClick(ad)}
                    >
                        <img
                            loading="lazy"
                            src={ad.thumbnail_url}
                            className="img-fluid w-100 h-100"
                            style={{ objectFit: "cover" }}
                            alt=""
                        />

                        {ad.is_catalog && (
                            <div className="position-absolute top-0 end-0 text-white px-1 small rounded">
                                🛒
                            </div>
                        )}

                        {(ad.media_type === "video" ||
                            ad.media_type === "fb_video") && (
                            <div className="position-absolute top-50 start-50 translate-middle">
                                <div
                                    className="bg-dark bg-opacity-75 rounded-circle d-flex justify-content-center align-items-center"
                                    style={{ width: "34px", height: "34px" }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        fill="#fff"
                                        viewBox="0 0 20 20"
                                    >
                                        <path d="M6.5 5.5l7 4-7 4v-8z" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>
                    <span className="badge bg-secondary">
                                        {ad.media_type}
                                      </span>
                </td>

                {/* Название + теги */}
                <td className="text-start td-name w-100">
                    <div className="d-flex align-items-center justify-content-between mb-1 w-100">
                        <div style={{ minWidth: "240px" }}>{ad.ad_name}</div>

                        <div>
                            <ProductAutocomplete
                                products={products}
                                onSelect={(product) => {
                                    const { ids } = parseAdName(ad.ad_name);
                                    if (!ids.includes(product.product_id)) {
                                        const newIds = [...ids, product.product_id];
                                        updateAdEverywhere(ad, newIds);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <div>
                        {ad.selectedProducts?.map((p, idx) => (
                            <span
                                key={`${ad.ad_id}_${p.id}_${idx}`}
                                className="badge bg-primary me-1 mb-1"
                            >
                            {p.name}
                                <span
                                    className="ms-1"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        const { ids } = parseAdName(ad.ad_name);
                                        const newIds = ids.filter((id) => id !== p.id);
                                        updateAdEverywhere(ad, newIds);
                                    }}
                                >
                              ×
                            </span>
                          </span>
                        ))}
                    </div>
                </td>

                <td className="text-end">${Number(ad.spend || 0).toFixed(2)}</td>
                <td className="text-end">{ad.clicks}</td>
                <td className="text-end">{ad.impressions}</td>
            </tr>

            {openedCatalogAdId === ad.ad_id && (
                <tr>
                    <td colSpan="7">
                        <CatalogTable rows={catalogStats} />
                    </td>
                </tr>
            )}
        </React.Fragment>
    ))}
    </tbody>

    <tfoot>
    <tr className="fw-bold bg-light">
        <td colSpan="7" className="text-end">
            Усього: ${totals.spend.toFixed(2)} · 👆 {totals.clicks} · 👁{" "}
            {totals.impressions}
        </td>
    </tr>
    </tfoot>
</table>
                        )}

                </>
            )}

            {/* ===================== MODAL ====================== */}
            <div className="modal fade" id="creativeModal" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{modalContent?.media_type}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={() => {
                                    const video =
                                        document.querySelector("#creativeModal video");
                                    if (video) video.pause();
                                }}
                            ></button>
                        </div>

                        <div className="modal-body text-center">
                            {/* IMAGE */}
                            {modalContent?.media_type === "image" && (
                                <img
                                    src={modalContent.bigImage}
                                    className="img-fluid rounded"
                                    style={{ maxHeight: "75vh" }}
                                    alt=""
                                />
                            )}

                            {/* IG VIDEO → показываем в модалке */}
                            {modalContent?.media_type === "video" && (
                                <video
                                    src={modalContent.media_url}
                                    controls
                                    autoPlay
                                    muted
                                    playsInline
                                    className="w-100 rounded"
                                    style={{ maxHeight: "75vh" }}
                                />
                            )}

                            {/* IG CAROUSEL */}
                            {modalContent?.media_type === "carousel" && (
                                <div className="carousel-container">
                                    <div className="d-flex justify-content-between mb-3">
                                        <button
                                            className="btn btn-light"
                                            onClick={() =>
                                                setModalContent((prev) => ({
                                                    ...prev,
                                                    slide: Math.max((prev.slide ?? 0) - 1, 0),
                                                }))
                                            }
                                        >
                                            ←
                                        </button>

                                        <button
                                            className="btn btn-light"
                                            onClick={() =>
                                                setModalContent((prev) => ({
                                                    ...prev,
                                                    slide: Math.min(
                                                        (prev.slide ?? 0) + 1,
                                                        (prev.carousel?.length ?? 1) - 1,
                                                    ),
                                                }))
                                            }
                                        >
                                            →
                                        </button>
                                    </div>

                                    {modalContent.carousel &&
                                        modalContent.carousel.length > 0 &&
                                        (() => {
                                            const slide = modalContent.slide ?? 0;
                                            const item = modalContent.carousel[slide];

                                            if (item.media_type === "video") {
                                                return (
                                                    <video
                                                        src={modalContent.media_url}
                                                        controls
                                                        autoPlay
                                                        muted
                                                        playsInline
                                                        className="w-100 rounded"
                                                        style={{ maxHeight: "75vh" }}
                                                    />
                                                );
                                            }

                                            return (
                                                <img
                                                    src={item.media_url}
                                                    className="img-fluid rounded"
                                                    style={{ maxHeight: "75vh" }}
                                                    alt=""
                                                />
                                            );
                                        })()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FacebookPage;
